
import { GoogleGenAI, Type } from "@google/genai";
import { Product, Store, DynamicPricingConfig } from '../types';
import { storage } from '../lib/storage';

export const dynamicPricingService = {
  getStoreConfig: (storeId: string): DynamicPricingConfig => {
    const store = storage.getStoreConfig(storeId);
    return store.dynamicPricing || {
      enabled: false,
      maxDiscountPercentage: 30,
      minDiscountPercentage: 0,
      strategy: 'BALANCED',
      autoApply: false
    };
  },

  saveConfig: (storeId: string, config: DynamicPricingConfig) => {
    const store = storage.getStoreConfig(storeId);
    storage.saveStoreConfig({ ...store, dynamicPricing: config });
  },

  calculateAdjustedPrice: (product: Product, config: DynamicPricingConfig): number => {
    if (!config.enabled || !product.dynamicPriceAdjustment) return product.price;
    const discount = Math.min(config.maxDiscountPercentage, product.dynamicPriceAdjustment);
    return product.price * (1 - discount / 100);
  },

  generatePriceRecommendations: async (storeId: string, products: Product[]) => {
    const config = dynamicPricingService.getStoreConfig(storeId);
    if (!config.enabled) return null;

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const productData = products.map(p => ({
        id: p.id,
        name: p.name,
        price: p.price,
        stock: p.stock,
        wishlistCount: p.wishlistCount || 0,
        createdAt: p.createdAt
      }));

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Analyze these products for a ${config.strategy} dynamic pricing strategy.
        Rules:
        1. High stock + Low sales = Higher discount.
        2. High wishlist + Low stock = Minimum or No discount.
        3. Recently added = No discount.
        4. Strategy: ${config.strategy} (Max: ${config.maxDiscountPercentage}%).
        
        Inventory Data:
        ${JSON.stringify(productData)}
        
        Return a JSON array of objects: [{"productId": "...", "recommendedDiscount": number (0 to ${config.maxDiscountPercentage}), "reason": "..."}]`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              recommendations: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    productId: { type: Type.STRING },
                    recommendedDiscount: { type: Type.NUMBER },
                    reason: { type: Type.STRING }
                  },
                  required: ["productId", "recommendedDiscount", "reason"]
                }
              }
            }
          }
        }
      });

      const parsed = JSON.parse(response.text);
      const recommendations = parsed.recommendations || [];
      
      if (config.autoApply) {
        const allProducts = storage.getProducts();
        const updated = allProducts.map(p => {
          if (p.storeId === storeId) {
            const rec = recommendations.find((r: any) => r.productId === p.id);
            return { ...p, dynamicPriceAdjustment: rec ? rec.recommendedDiscount : 0 };
          }
          return p;
        });
        storage.saveProducts(updated);
      }

      return recommendations;
    } catch (error) {
      console.error("AI Pricing Error:", error);
      return null;
    }
  }
};
